<template>
  <div class="results-widget-section">
    <div :class="titleClasses">{{ title }}</div>

    <div v-if="errorMessage" class="error-label text-center">{{errorMessage}}</div>

    <div :class="['data-description', {['text-center']: isAutoconnect}]">
      updates every 15 minutes, last updated {{lastUpdatedTime}}
    </div>

    <table class="track-table">
      <thead>
        <tr>
          <th class="text-center hide-for-small-only">{{totalSentText}}</th>
          <th class="text-center">clicks</th>
          <th class="text-center hide-for-small-only">visits</th>
          <th class="text-center">visitor spend</th>
          <th class="text-center hide-for-small-only">extension</th>
          <th class="text-center hide-for-small-only">actions</th>
          <th v-if="isAutoconnect && attachedMedia" class="text-center">{{attachedMedia.label}}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="text-center hide-for-small-only sms-sent">{{totalSent}}</td>
          <td class="text-center clicks-calculated">
            <span v-if="isAutoconnect">{{clicksCalc}}</span>
            <a v-else @click="openClicksModal">{{clicksCalc}}</a>
          </td>
          <td class="text-center hide-for-small-only visits-calculated">{{visitsCalc}}</td>
          <td class="text-center visitor-spend">{{visitSpend}}</td>
          <td class="text-center hide-for-small-only action-type">{{rewardType || extensionName}}</td>
          <td class="text-center hide-for-small-only redemptions-calculated">{{redemptionsCalc}}</td>
          <td v-if="isAutoconnect && attachedMedia" class="text-center visitor-spend">{{attachedMedia.media}}</td>
        </tr>
      </tbody>
    </table>

    <modal
        v-if="showClicksModal"
        :showClose="true"
        @close="showClicksModal = false"
        wrapperClasses="clicks-modal">
        <h2 slot="header" class="title">campaign #{{ campaignId }}</h2>
        <div slot="body" class="body">
          <h3>This campaign was sent to {{ totalSent }} {{ totalSent | pluralize('recipient') }}. Send a campaign to members based on how they reacted to your campaign</h3>
        </div>
        <div slot="action" class="action-container">
          <div class="actions">
            {{ clicksCount }} {{ clicksCount | pluralize('member') }} <b>clicked</b> on the campaign
            <button class="button larger modal-click-action" @click="redirectToNewCampaignPage('campaign_click')">send {{ clicksCount | pluralize('this member', 'these members') }} a campaign</button>
          </div>
          <div class="actions">
            {{ didNotClickCount }} {{ didNotClickCount | pluralize('member') }} <b>did not click</b> on the campaign
            <button class="button larger modal-click-action" @click="redirectToNewCampaignPage('no_campaign_click')">send {{ didNotClickCount | pluralize('this member', 'these members') }} a campaign</button>
          </div>
        </div>

    </modal>
  </div>
</template>

<script>
  import AxiosMixin from '../../mixins/AxiosMixin'
  import { isObject } from '../../utilities'
  import { mapState } from 'vuex'
  import Modal from '../../components/shared/Modal'
  import moment from 'moment-timezone'
  import { campaignAPI } from "../../services/api/campaignAPI"

  export default {
    props: {
      attachedMedia: Object,
      extensionName: String,
      isAutoconnect: {
        default: false,
        type: Boolean
      },
      isTracked: Boolean,
      rewardType: String,
      titleClasses: {
        default: 'data-title',
        type: String
      },
      totalSentText: {
        default: 'sms sent',
        type: String
      },
      url: String
    },

    name: 'StatsWidget',

    components: {
      Modal
    },

    mixins: [AxiosMixin],

    data() {
      return {
        clicksCount: 'calculating',
        clicksPercentage: null,
        errorMessage: null,
        errorText: 'we encountered an issue loading the campaign stats.',
        lastUpdated: null,
        redemptionCount: 'calculating',
        redemptionPercentage: null,
        showClicksModal: false,
        totalSent: 'calculating',
        visitCount: 'calculating',
        visitCountPercentage: null,
        visitSpend: 'calculating',
        visitCalculations: null
      }
    },

    created() {
      this.loadResults()
    },

    computed: {
      campaignId(){
        return this.$route.params.id
      },

      clicksCalc: function () {
        return this.configureStatsString('clicksCount', 'clicksPercentage')
      },

      didNotClickCount(){
        return this.totalSent - this.clicksCount
      },

      lastUpdatedTime: function () {
        if (this.lastUpdated && moment.tz) {
          return moment.tz(this.lastUpdated, this.currentMerchant.merchant_time_zone).format('h:mma')
        }
      },

      redemptionsCalc: function () {
        return this.configureStatsString('redemptionCount', 'redemptionPercentage')
      },

      title() {
        return this.isAutoconnect ? 'message results' : 'campaign results'
      },

      visitsCalc: function () {
        return this.configureStatsString('visitCount', 'visitCountPercentage')
      },

      ...mapState([
        'currentMerchant'
      ]),
    },

    methods: {
      configureStatsString(count, percentage) {
        const showNoClicks = count === 'clicksCount' && !this.isTracked,
          showNoRedemptions = count === 'redemptionCount' && (!this.rewardType || this.rewardType === 'instant points'),
          showDashes = showNoClicks || showNoRedemptions
        let statsString = 'calculating'
        //if (showDashes) { return '--' }
        if (this.lastUpdated) { statsString = `${this[count]}(${this[percentage].toFixed(1)}%)` }
        return statsString
      },

      loadResults() {
        this.url && this.axiosMethod({url: this.url, onSuccess: this.setResultsData, onError: this.setLoadError })
      },

      openClicksModal() {
        this.showClicksModal = true
      },

      async redirectToNewCampaignPage(click_type) {
        const resp = await campaignAPI.postCreateCampaignWithSegments(this.campaignId, click_type)
        location.assign(resp.edit_path)
      },

      setLoadError(error) {
        this.errorMessage = `${this.errorText} ${error}`
      },

      setResultsData(data) {
        if (data && isObject(data)) {
          this.clicksCount = data?.clicks?.count || 0;
          this.clicksPercentage = data?.clicks?.percentage
          this.redemptionCount = data?.redemptions_rate_calculation?.count
          this.redemptionPercentage = data?.redemptions_rate_calculation?.percentage
          this.visitCount = data?.visits_calculations?.count
          this.visitCountPercentage = data?.visits_calculations?.count_percentage
          this.visitSpend = data?.visits_calculations?.spend
          this.totalSent = data.sms_sent
          this.$emit('setCreditsUsed', data.total_credits)
          this.lastUpdated = data.stats_at
          this.errorMessage = null
        } else {
          this.errorMessage = this.errorText
        }
      }
    }
  }
</script>
